import type {ComputedRef, Ref, ShallowRef} from 'vue';
import {computed, ref, shallowRef} from '#imports';

export interface UseAbortableOptions {
    automatedAbort: boolean;
}

const defaultOptions: UseAbortableOptions = {
    automatedAbort: true,
};

/**
 * Transform any OpenAPI request into a abortable request
 *
 * @param options - Optional parameter to customize the behavior
 * @param options.automatedAbort - If true the previous request will be automatically aborted if a new one is started, defaults to true.
 *
 * @usage
 *
 * ```ts
 * import {UserApi} from '~/openapi';
 *
 * export const userStore = defineStore('user', () => {
 *
 *     const {execute, pending} = useAbortable();
 *
 *     async function searchUser(query: string) {
 *         // This function will be aborted if a new search is triggered, and result will be undefined
 *         const result = await execute((signal) => UserApi.fetchUsers({query}, {signal}));
 *         if (result) {
 *             users.value = result
 *         }
 *     }
 * })
 * ```
 */
export function useAbortable(options: Partial<UseAbortableOptions> = {}): {
    execute: <ResponseType>(abortableFunction: (signal: AbortSignal) => ResponseType | Promise<ResponseType>) => Promise<ResponseType | undefined>;
    abort: () => void;
    aborted: Ref<boolean>;
    pending: ComputedRef<boolean>;
    abortController: ShallowRef<AbortController | null>;
    error: Ref<any | null>;
} {
    const lastRequestController = shallowRef<AbortController | null>(null);
    const aborted = ref(false);
    const error = ref<any | null>(null);

    const pending = computed<boolean>(() => !!lastRequestController.value);

    const finalOptions: UseAbortableOptions = {
        ...defaultOptions,
        ...options,
    };

    /**
     * Undefined result means the request was aborted
     */
    async function execute<ResponseType>(
        abortableFunction: (signal: AbortSignal) => ResponseType | Promise<ResponseType>,
    ): Promise<ResponseType | undefined> {
        let signal: AbortSignal | null = null;
        const currentRequestController = new AbortController();

        try {
            if (finalOptions.automatedAbort) {
                abort();
            }
            lastRequestController.value = currentRequestController;
            signal = currentRequestController.signal;

            const result = await abortableFunction(signal);
            error.value = null;
            return result;
        } catch (exception) {
            if (!signal || !signal.aborted) {
                error.value = exception;
                return Promise.reject(exception);
            }
        } finally {
            if (currentRequestController === lastRequestController.value) {
                lastRequestController.value = null;
            }
            aborted.value = !!signal?.aborted;
        }
    }

    function abort() {
        if (lastRequestController.value) {
            lastRequestController.value.abort();
        }
    }

    return {
        execute,
        abort,
        aborted,
        pending,
        error,
        abortController: lastRequestController,
    };
}
